import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CardBody, ReadOnlyRichText } from 'src/components/atoms';
import { ShadowsSize } from 'src/components/foundations/tokens';
import { FormManagementLanguageChips } from 'src/components/pages/CoiFormManagement/FormManagementLanguageChips';
import type {
  FormStatusTab,
  FormType,
} from 'src/components/pages/CoiFormManagement/types';
import { stripHtml } from 'src/utils/stripHtml';
import { CategoryDescriptionPopup } from 'src/components/organisms/CategoryDescriptionPopup';
import {
  CardTagsRow,
  CategoryCardBodyContent,
  CategoryCardTextGroup,
  CategoryCardTopSection,
  CategoryDescriptionSlot,
  CategoryName,
  CategoryRichTextClamped,
  DescriptionInlineToggle,
  DescriptionSuffixStack,
  InteractiveCardContainer,
} from './ActiveCategoryCard.styles';

export interface ActiveCategory {
  id: string;
  name: string;
  description?: string;
  languages?: string;
  activeFormsCount: number;
  primaryForm?: {
    id: string;
    type: FormType;
    status: FormStatusTab;
  };
}

interface Props {
  category: ActiveCategory;
  maxVisibleLanguages?: number;
  allowExpand?: boolean;
  onCardClick?: () => void;
  'data-testid'?: string;
}

function escapeHtmlForRichText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function categoryDescriptionToQuillHtml(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  if (stripHtml(trimmed).trim() !== trimmed.trim()) {
    return trimmed;
  }
  const blocks = trimmed.split(/\n+/).filter((b) => b.length > 0);
  if (blocks.length === 0) {
    return '';
  }
  return blocks.map((b) => `<p>${escapeHtmlForRichText(b)}</p>`).join('');
}

function parseLanguageNames(languages?: string): string[] {
  if (!languages?.trim()) {
    return [];
  }
  return languages
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export const ActiveCategoryCard = ({
  category,
  maxVisibleLanguages = 3,
  allowExpand = true,
  onCardClick,
  ...props
}: Props) => {
  const { name, description, languages } = category;
  const [needsDescriptionToggle, setNeedsDescriptionToggle] = useState(false);
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const descriptionClampRef = useRef<HTMLDivElement>(null);
  const showMoreButtonRef = useRef<HTMLButtonElement>(null);

  const descriptionHtml = useMemo(
    () => categoryDescriptionToQuillHtml(description ?? ''),
    [description],
  );

  const plainDescription = useMemo(
    () => stripHtml(descriptionHtml).replace(/\s+/g, ' ').trim(),
    [descriptionHtml],
  );

  const hasDescriptionContent = plainDescription.length > 0;

  const languageNames = useMemo(
    () => parseLanguageNames(languages),
    [languages],
  );

  useEffect(() => {
    if (!allowExpand || !hasDescriptionContent) {
      setNeedsDescriptionToggle(false);
      return;
    }
    const el = descriptionClampRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const overflow = el.scrollHeight > el.clientHeight + 1;
      setNeedsDescriptionToggle(overflow);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [descriptionHtml, hasDescriptionContent, allowExpand]);

  const showTagsRow = languageNames.length > 0;

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!onCardClick) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onCardClick();
      }
    },
    [onCardClick],
  );

  return (
    <InteractiveCardContainer
      hasShadow={false}
      $shadowSize={ShadowsSize.small}
      $width="100%"
      $margin="0"
      $clickable={!!onCardClick}
      data-testid={props['data-testid']}
      aria-label={name}
      onClick={onCardClick}
      onKeyDown={onCardClick ? handleCardKeyDown : undefined}
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
    >
      <CardBody>
        <CategoryCardBodyContent>
          <CategoryCardTopSection>
            <CategoryCardTextGroup>
              <CategoryName title={name}>{name}</CategoryName>
              {hasDescriptionContent && (
                <CategoryDescriptionSlot>
                  <CategoryRichTextClamped ref={descriptionClampRef}>
                    <ReadOnlyRichText
                      value={descriptionHtml}
                      data-testid={`${props['data-testid']}-description-clamped-richtext`}
                    />
                  </CategoryRichTextClamped>
                  {needsDescriptionToggle && (
                    <DescriptionSuffixStack
                      data-testid={`${props['data-testid']}-description-suffix`}
                    >
                      <DescriptionInlineToggle
                        ref={showMoreButtonRef}
                        type="button"
                        aria-expanded={showDescriptionPopup}
                        aria-label="Show full description"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDescriptionPopup(true);
                        }}
                        data-testid={`${props['data-testid']}-see-more`}
                      >
                        Show more
                      </DescriptionInlineToggle>
                    </DescriptionSuffixStack>
                  )}
                </CategoryDescriptionSlot>
              )}
            </CategoryCardTextGroup>
          </CategoryCardTopSection>
          {showTagsRow && (
            <CardTagsRow data-testid={`${props['data-testid']}-tags-row`}>
              {languageNames.length > 0 && (
                <FormManagementLanguageChips
                  languages={languageNames}
                  maxVisible={maxVisibleLanguages}
                  data-testid={`${props['data-testid']}-language-chips`}
                />
              )}
            </CardTagsRow>
          )}
        </CategoryCardBodyContent>
      </CardBody>
      {showDescriptionPopup && (
        <CategoryDescriptionPopup
          title={name}
          descriptionHtml={descriptionHtml}
          onClose={() => setShowDescriptionPopup(false)}
          triggerRef={showMoreButtonRef}
        />
      )}
    </InteractiveCardContainer>
  );
};
